import type { CSSProperties } from 'react';

/**
 * Material Symbols Outlined wrapper — the OneView (Galaxy) icon system.
 *
 * Replaces every `lucide-react` import across the app. The Material Symbols
 * font itself is loaded in `index.html`; this component is a thin wrapper
 * that renders the icon as a styled <span>.
 *
 * Compatibility notes:
 *   - `size` defaults to 20 (matches OneView body-line height and most
 *     lucide call sites which used `size={16}`/`size={20}`).
 *   - `fill` flips the variation axis to a filled glyph for active/selected
 *     states (used by sidebar pill nav).
 *   - `className` is forwarded so existing layout/colour utilities
 *     (`text-brand-500`, `mr-2`, etc.) continue to work as drop-in
 *     replacements for `<XIcon className="..." />`.
 *   - `aria-hidden` defaults to true; pass `aria-label` to surface meaning
 *     when the icon is the only signal (e.g. an icon-only button).
 *
 * Common lucide → Material Symbols mappings used in this codebase:
 *   RefreshCw → sync,  Trash2 → delete,  Pencil → edit,  Plus → add,
 *   X → close,  BookText → menu_book,  AlertTriangle → warning,
 *   AlertCircle → error,  UploadCloud → cloud_upload,  Ban → block,
 *   Check → check,  Loader2 → progress_activity,  Minus → remove,
 *   ChevronDown → expand_more,  ChevronRight → chevron_right,
 *   ChevronUp → expand_less,  GitBranch → device_hub,  Code → code,
 *   Link → link,  ExternalLink → open_in_new,  FileText → description,
 *   Files → folder_copy,  File → draft,  Image → image,  Copy → content_copy,
 *   ThumbsUp → thumb_up,  ThumbsDown → thumb_down,  History → history,
 *   Search → search,  Mail → mail,  ArrowUpDown → unfold_more,
 *   MessageSquareText → forum,  Settings → settings,  LogOut → logout,
 *   Menu → menu,  CheckCircle2 → check_circle.
 */
export interface IconProps {
  name: string;
  size?: number;
  fill?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Accessible label — when set, removes aria-hidden. */
  'aria-label'?: string;
  /** Click handler for icon-only affordances (paired with role + aria-label). */
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  title?: string;
}

export function Icon({
  name,
  size = 20,
  fill = false,
  className = '',
  style,
  'aria-label': ariaLabel,
  onClick,
  title,
}: IconProps) {
  return (
    <span
      className={`material-symbols-outlined${fill ? ' msi-fill' : ''} ${className}`.trim()}
      style={{ fontSize: size, ...style }}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      onClick={onClick}
      title={title}
    >
      {name}
    </span>
  );
}

/**
 * Drop-in replacement factory for the lucide-react `<XIcon>` component
 * pattern. Lets us write:
 *
 *   const RefreshCwIcon = lucideShim('sync');
 *
 * and keep the existing JSX usage `<RefreshCwIcon className="w-4 h-4" />`
 * working without per-site rewrites. Width/height tailwind classes don't
 * affect a font glyph; we read them out and turn them into a `size` value
 * when they're recognisable (`w-3` → 12, `w-4` → 16, `w-5` → 20, etc.).
 */
const TW_SIZE: Record<string, number> = {
  'w-3': 12,
  'h-3': 12,
  'w-3.5': 14,
  'h-3.5': 14,
  'w-4': 16,
  'h-4': 16,
  'w-5': 20,
  'h-5': 20,
  'w-6': 24,
  'h-6': 24,
  'w-7': 28,
  'h-7': 28,
  'w-8': 32,
  'h-8': 32,
};

export interface LucideShimProps {
  className?: string;
  size?: number;
  fill?: boolean;
  style?: CSSProperties;
  'aria-label'?: string;
  // Accept string OR boolean — JSX often passes "true"/"false" string literals.
  'aria-hidden'?: boolean | 'true' | 'false';
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  strokeWidth?: number; // lucide prop, ignored
}

export function lucideShim(name: string, defaults: { fill?: boolean } = {}) {
  return function LucideShim({ className = '', size, fill, ...rest }: LucideShimProps) {
    let resolvedSize = size;
    if (!resolvedSize) {
      for (const token of className.split(/\s+/)) {
        if (TW_SIZE[token]) {
          resolvedSize = TW_SIZE[token];
          break;
        }
      }
    }
    return (
      <Icon
        name={name}
        size={resolvedSize ?? 20}
        fill={fill ?? defaults.fill}
        className={className}
        {...rest}
      />
    );
  };
}
